/**
 * Vertical text menu with keyboard (↑↓/WS, Enter/Space/Z, Esc/X) and pointer (hover/click) selection.
 * Selected row sits on a lacquer bar. Used by title, chapter end, pause, choices and slot panels.
 */
import Phaser from 'phaser';
import { audio } from '@/audio/index';
import { uiText } from './text';
import { Css, Slices } from './theme';

export interface MenuItem { label: string; onSelect?: () => void; disabled?: boolean; hint?: string }

export interface MenuListOptions {
  x: number; y: number;
  items: MenuItem[];
  width?: number;
  rowHeight?: number;
  fontSize?: number;
  align?: 'center' | 'left';
  keyboard?: boolean;
  wrap?: boolean;
  onCancel?: () => void;
  onChange?: (index: number) => void;
  /** When it returns true the list ignores input (another modal is on top). */
  isBlocked?: () => boolean;
  depth?: number;
  showBar?: boolean;
  color?: string; selectedColor?: string; disabledColor?: string;
}

/** Gap between a centred label's right edge and its trailing hint. */
export const HINT_GAP = 8;

export class MenuList {
  readonly container: Phaser.GameObjects.Container;
  private rows: { text: Phaser.GameObjects.Text; zone: Phaser.GameObjects.Zone; hint?: Phaser.GameObjects.Text }[] = [];
  private bar: Phaser.GameObjects.NineSlice;
  private index = 0;
  private items: MenuItem[];
  private readonly keyHandlers: [string, (e: KeyboardEvent) => void][] = [];
  private destroyed = false;

  constructor(private readonly scene: Phaser.Scene, private readonly opts: MenuListOptions) {
    this.items = opts.items;
    this.container = scene.add.container(Math.round(opts.x), Math.round(opts.y)).setDepth(opts.depth ?? 0);
    const w = opts.width ?? 200;
    const rh = opts.rowHeight ?? 26;
    this.bar = scene.add.nineslice(0, 0, Slices.bar.key, undefined, w, rh - 4, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0.5).setVisible(opts.showBar !== false);
    this.container.add(this.bar);
    this.build();
    if (opts.keyboard !== false) this.bindKeys();
    this.select(this.firstEnabled(0, 1), false);
  }

  private build(): void {
    for (const r of this.rows) { r.text.destroy(); r.zone.destroy(); r.hint?.destroy(); }
    this.rows = [];
    const w = this.opts.width ?? 200;
    const rh = this.opts.rowHeight ?? 26;
    const align = this.opts.align ?? 'center';
    this.items.forEach((item, i) => {
      const y = i * rh;
      const x = align === 'center' ? 0 : -w / 2 + 12;
      const text = uiText(this.scene, x, y, item.label, { fontSize: `${this.opts.fontSize ?? 16}px`, color: item.disabled ? (this.opts.disabledColor ?? Css.dim) : (this.opts.color ?? Css.parchment) });
      text.setOrigin(align === 'center' ? 0.5 : 0, 0.5);
      const zone = this.scene.add.zone(0, y, w, rh).setOrigin(0.5).setInteractive({ useHandCursor: !item.disabled });
      zone.on('pointerover', () => { if (!this.blocked() && !item.disabled) this.select(i); });
      zone.on('pointerdown', () => { if (this.blocked() || item.disabled) return; this.select(i); this.confirm(); });
      let hint: Phaser.GameObjects.Text | undefined;
      if (item.hint) {
        // Centred rows: the hint trails the label (never under it); left rows: right-aligned in the row.
        hint = align === 'center'
          ? uiText(this.scene, Math.round(text.width / 2) + HINT_GAP, y, item.hint, { fontSize: '11px', color: Css.dim }).setOrigin(0, 0.5)
          : uiText(this.scene, w / 2 - 12, y, item.hint, { fontSize: '11px', color: Css.dim }).setOrigin(1, 0.5);
      }
      this.container.add([zone, text]);
      if (hint) this.container.add(hint);
      const row: { text: Phaser.GameObjects.Text; zone: Phaser.GameObjects.Zone; hint?: Phaser.GameObjects.Text } = { text, zone };
      if (hint) row.hint = hint;
      this.rows.push(row);
    });
  }

  private blocked(): boolean { return this.destroyed || (this.opts.isBlocked?.() ?? false); }

  private bindKeys(): void {
    const kb = this.scene.input.keyboard;
    if (!kb) return;
    const add = (key: string, fn: (e: KeyboardEvent) => void) => {
      const wrapped = (e: KeyboardEvent) => { if (!this.blocked() && this.container.visible) fn(e); };
      kb.on(`keydown-${key}`, wrapped);
      this.keyHandlers.push([`keydown-${key}`, wrapped]);
    };
    const up = () => this.move(-1);
    const down = () => this.move(1);
    add('UP', up); add('W', up); add('DOWN', down); add('S', down);
    const confirm = () => this.confirm();
    add('ENTER', confirm); add('SPACE', confirm); add('Z', confirm);
    const cancel = () => { if (this.opts.onCancel) { audio().sfx('ui-cancel'); this.opts.onCancel(); } };
    add('ESC', cancel); add('X', cancel); add('BACKSPACE', cancel);
  }

  private firstEnabled(from: number, dir: 1 | -1): number {
    const n = this.items.length;
    if (n === 0) return 0;
    let i = ((from % n) + n) % n;
    for (let k = 0; k < n; k++) {
      if (!this.items[i]?.disabled) return i;
      i = (i + dir + n) % n;
    }
    return 0;
  }

  move(dir: 1 | -1): void {
    if (this.items.length === 0) return;
    let next = this.index + dir;
    if (this.opts.wrap === false) next = Phaser.Math.Clamp(next, 0, this.items.length - 1);
    const target = this.firstEnabled(next, dir);
    if (target !== this.index) { audio().sfx('ui-move'); this.select(target); }
  }

  select(i: number, notify = true): void {
    if (this.items.length === 0) return;
    this.index = ((i % this.items.length) + this.items.length) % this.items.length;
    const rh = this.opts.rowHeight ?? 26;
    this.bar.setPosition(0, this.index * rh);
    this.rows.forEach((r, k) => {
      const item = this.items[k];
      r.text.setColor(item?.disabled ? (this.opts.disabledColor ?? Css.dim) : k === this.index ? (this.opts.selectedColor ?? Css.white) : (this.opts.color ?? Css.parchment));
    });
    if (notify) this.opts.onChange?.(this.index);
  }

  confirm(): void {
    const item = this.items[this.index];
    if (!item || item.disabled) return;
    audio().sfx('ui-confirm');
    item.onSelect?.();
  }

  get selectedIndex(): number { return this.index; }
  get height(): number { return this.items.length * (this.opts.rowHeight ?? 26); }
  /** Row geometry in container-local pixels (verification: the hint must start after the label). */
  rowGeometry(i: number): { labelLeft: number; labelRight: number; hintLeft: number | null; hintRight: number | null } | null {
    const r = this.rows[i];
    if (!r) return null;
    const b = r.text.getBounds();
    const labelLeft = b.x - this.container.x, labelRight = b.right - this.container.x;
    if (!r.hint) return { labelLeft, labelRight, hintLeft: null, hintRight: null };
    const h = r.hint.getBounds();
    return { labelLeft, labelRight, hintLeft: h.x - this.container.x, hintRight: h.right - this.container.x };
  }

  setItems(items: MenuItem[]): void {
    this.items = items;
    this.build();
    this.select(this.firstEnabled(Math.min(this.index, Math.max(0, items.length - 1)), 1), false);
  }

  setVisible(v: boolean): this { this.container.setVisible(v); return this; }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    const kb = this.scene.input.keyboard;
    for (const [name, fn] of this.keyHandlers) kb?.off(name, fn);
    this.keyHandlers.length = 0;
    this.container.destroy(true);
  }
}
